import { ReactNode } from 'react';
import cn from 'classnames';
import { compact, concat, set, sortBy } from 'lodash-es';
import { MdOutlineLuggage } from 'react-icons/md';
import { SOrganGroup } from '@lp/features/Scheduler/SchedulerSurgeons/types';
import {
  regionRelatedSShiftTypes,
  singleSShiftTypes,
  sShiftTypeNames,
} from '@lp/features/Scheduler/SchedulerSurgeons/constants';
import {
  RegionsDataDictionary,
  ServiceRulesViolations,
  ServiceViolationItem,
  ShiftSiblingData,
  ViolationDayType,
} from '@lp/features/Scheduler/types';
import {
  getObjectEntries,
  isDifferentHrSource,
  toLocalDateFromApiDateKey,
  toZonedISODateKey,
} from '@lp/features/Scheduler/utils';
import { getShiftTypeServiceViolationMessagesByOrgan } from '@lp/features/Scheduler/validation.utils';
import {
  Region,
  SetSShiftData,
  ShiftStatus,
  ShiftType,
  ShiftTypeScheduleValidationResult,
  SRegion,
  SResource,
  SResourcesWithShifts,
  SShift,
  SShiftSelectPayload,
  SShiftType,
} from '@/api/apis/resources/schedule';
import { OrganType } from '@/api/apis/case';
import { organSuffixes } from '@/constants/case/case.constants';

export const getOrganGroupFromOrgan = (organ?: OrganType | null) => {
  switch (organ) {
    case OrganType.Liver:
      return SOrganGroup.Liver;
    case OrganType.Heart:
    case OrganType.Lungs:
      return SOrganGroup.HeartAndLungs;
    default:
      return null;
  }
};

export const getOrganGroupToRegionsMap = (regionList: SRegion[]) => {
  const liverRegionList: SRegion[] = [];
  const heartAndLungsRegionList: SRegion[] = [];

  regionList.forEach((region) => {
    const organsSet = new Set(region.organs);

    if (organsSet.has(OrganType.Liver)) liverRegionList.push(region);
    if (organsSet.has(OrganType.Heart) || organsSet.has(OrganType.Lungs)) heartAndLungsRegionList.push(region);
  });

  return {
    [SOrganGroup.Liver]: liverRegionList,
    [SOrganGroup.HeartAndLungs]: heartAndLungsRegionList,
  };
};

export const getSRegionToResourceMap = (
  shiftsData: SResourcesWithShifts,
  regionToAssignedMap: Record<string, string[]>,
) => {
  const regionToResourceMap: Record<string, SResource[]> = {};

  Object.values(shiftsData).forEach((data) => {
    if (data?.resource?.regionId) {
      const regionsIdsFromShifts = Object.values(data.days).map((s) => s.regionId);
      const resourceRegionIds = Array.from(new Set([data.resource.regionId, ...regionsIdsFromShifts]));

      resourceRegionIds.forEach((regionId) => {
        if (regionId) {
          const arr = regionToResourceMap[regionId] ?? [];
          regionToResourceMap[regionId] = [...arr, data.resource];
        }
      });
    }
  });

  Object.entries(regionToAssignedMap).forEach(([regionId, userIds]) => {
    const currentList = regionToResourceMap[regionId] ?? [];
    const filteredResources = currentList.filter((r) => !userIds.includes(r.id));
    const assignedResources = userIds.map((id) => shiftsData[id]?.resource);

    regionToResourceMap[regionId] = [...filteredResources, ...assignedResources];
  });

  return regionToResourceMap;
};

export const getSSiblingDataGetter =
  (targetRegionId: string, regionsDictionary: Record<string, Region>) =>
  (shift: SShift, prevShift?: SShift): ShiftSiblingData => {
    const isSingle = singleSShiftTypes.includes(shift.shiftType);
    const sameShiftType = shift.shiftType === prevShift?.shiftType;
    const sameRegion = (shift.regionId ?? null) === (prevShift?.regionId ?? null);
    const isExternal = !!shift.regionId && shift.regionId !== targetRegionId;

    if (isExternal) {
      return {
        hasPrev: sameRegion,
        isExternal,
        id: shift.regionId!,
        name: regionsDictionary[shift.regionId!]?.name ?? '[UNKNOWN]', // Fallback for removed regions,
      };
    }

    if (isSingle || !sameShiftType || !sameRegion) return { hasPrev: false };

    if (shift.shiftType === SShiftType.NoCall) {
      if (shift.fixationType !== prevShift?.fixationType) return { hasPartialPrev: true };
    }

    if (isDifferentHrSource(shift, prevShift)) return { hasPartialPrev: true };

    return { hasPrev: true };
  };

export const getSResourceEmptyShift = (
  resourceId: string,
  daysInterval: Date[],
  shiftsData: SResourcesWithShifts,
  regionToResourceMap: Record<string, SResource[]>,
) => {
  const dayKeys = daysInterval.map(toZonedISODateKey);
  const segment = shiftsData[resourceId];
  if (!segment) return null;

  const regionId = Object.entries(regionToResourceMap).find(([, resources]) =>
    resources.some((r) => r.id === resourceId),
  )?.[0];

  if (regionId) {
    for (const dayKey of dayKeys) {
      if (!segment.days[dayKey]) {
        return {
          day: toLocalDateFromApiDateKey(dayKey),
          resourceId,
          regionId,
        };
      }
    }
  }

  return null;
};

export const getSShiftSetData = (
  shiftPayload: SShiftSelectPayload,
  resource?: SResource,
  _regionId?: string,
): SetSShiftData => {
  const isRegionRequired = regionRelatedSShiftTypes.includes(shiftPayload.shiftType);
  const regionId = isRegionRequired ? _regionId : undefined;
  const isTravel = isRegionRequired && resource?.regionId !== regionId;
  const organs = shiftPayload.shiftType === SShiftType.OnCall ? resource?.organs : undefined;

  return { ...shiftPayload, organs, regionId, isTravel };
};

export const transformRegionViolationsByOrgan = <T extends ShiftType>(
  violationsData?: ShiftTypeScheduleValidationResult<T>,
  regionsDictionary?: RegionsDataDictionary,
) => {
  const byOrganRegionMap = {} as Record<SOrganGroup, Record<string, ServiceRulesViolations>>;
  const regionsMessagesList: ServiceViolationItem[] = [];

  if (violationsData && regionsDictionary) {
    const violationDayKeys = ['dailyRulesViolations', 'weeklyRulesViolations'] as const;

    violationDayKeys.forEach((violationDayKey) => {
      const dayType = violationDayKey === 'dailyRulesViolations' ? ViolationDayType.Daily : ViolationDayType.Weekly;

      getObjectEntries(violationsData[violationDayKey]).forEach(([dayKey, serviceViolations]) => {
        Object.entries(serviceViolations.regionsRulesViolations).forEach(([regionId, shiftTypeViolations]) => {
          const title = regionsDictionary[regionId]?.name ?? '[UNKNOWN]';
          const messagesByOrgan = getShiftTypeServiceViolationMessagesByOrgan(shiftTypeViolations, dayKey, dayType);
          const liverGroupMessages = compact(concat(messagesByOrgan.global, messagesByOrgan.Liver));
          const heartAndLungsGroupMessages = compact(
            concat(messagesByOrgan.global, messagesByOrgan.Heart, messagesByOrgan.Lungs),
          );

          if (liverGroupMessages.length > 0) {
            const item: ServiceViolationItem = {
              id: regionId,
              day: dayKey,
              title,
              messages: liverGroupMessages,
              dayType,
            };

            set(byOrganRegionMap, [SOrganGroup.Liver, regionId, dayType, dayKey], item);
            regionsMessagesList.push(item);
          }

          if (heartAndLungsGroupMessages.length > 0) {
            const item: ServiceViolationItem = {
              id: regionId,
              day: dayKey,
              title,
              messages: heartAndLungsGroupMessages,
              dayType,
            };

            set(byOrganRegionMap, [SOrganGroup.HeartAndLungs, regionId, dayType, dayKey], item);
            regionsMessagesList.push(item);
          }
        });
      });
    });
  }

  return {
    byOrganRegionMap,
    regionsMessagesList: sortBy(regionsMessagesList, ['title', 'day']),
  };
};

export const getSShiftTypeColors = (shiftData?: SShift, isExternalRegion?: boolean) => {
  const { shiftType, shiftStatus, isPreSchedule } = shiftData ?? {};
  const isPlanning = shiftStatus === ShiftStatus.InPlanningProcess;
  const borderStyle = isPreSchedule ? 'border' : 'border border-dashed';

  if (isExternalRegion) {
    return {
      bg: 'bg-base-80',
      text: 'text-base-20',
      secondaryText: 'text-base-20',
      border: isPlanning || isPreSchedule ? cn(borderStyle, 'border-base-50') : 'border-transparent',
      icon: 'text-base-20',
    };
  }

  if (isPlanning || isPreSchedule) {
    switch (shiftType) {
      case SShiftType.Vacation:
      case SShiftType.Sick:
        return {
          bg: 'bg-warning-40/20',
          text: 'text-warning-5',
          secondaryText: 'text-warning-5',
          border: 'border-transparent',
          icon: 'text-warning-20',
        };
      case SShiftType.Unavailable:
        return {
          bg: 'bg-danger-40/10',
          text: 'text-danger-5',
          secondaryText: 'text-danger-20',
          border: 'border border-danger-20',
          icon: 'text-danger-30',
        };
      case SShiftType.NoCall:
        return {
          bg: 'bg-base-50/50',
          text: 'text-base-10',
          secondaryText: 'text-base-20',
          border: cn(borderStyle, 'border-base-20'),
          icon: 'text-base-30',
        };
      case SShiftType.OnCall:
        return {
          bg: 'bg-transparent',
          text: 'text-base-20',
          secondaryText: 'text-base-25',
          border: cn(borderStyle, 'border-base-20'),
          icon: 'text-base-30',
        };
      default:
        return {
          bg: 'bg-transparent',
          text: 'text-base-20',
          secondaryText: 'text-base-25',
          border: 'border-transparent',
          icon: 'text-base-5',
        };
    }
  }

  switch (shiftType) {
    case SShiftType.Vacation:
    case SShiftType.Sick:
      return {
        bg: 'bg-warning-40/20',
        text: 'text-warning-5',
        secondaryText: 'text-warning-5',
        border: 'border-transparent',
        icon: 'text-warning-20',
      };
    case SShiftType.Unavailable:
      return {
        bg: 'bg-danger-40/20',
        text: 'text-danger-5',
        secondaryText: 'text-danger-5',
        border: 'border-transparent',
        icon: 'text-danger-30',
      };
    case SShiftType.NoCall:
      return {
        bg: 'bg-base-50',
        text: 'text-base-10',
        secondaryText: 'text-base-20',
        border: 'border-transparent',
        icon: 'text-base-30',
      };
    case SShiftType.OnCall:
    default:
      return {
        bg: 'bg-transparent',
        text: 'text-base-20',
        secondaryText: 'text-base-25',
        border: 'border-transparent',
        icon: 'text-base-5',
      };
  }
};

const getTextWithIcon = (text: string, icon: ReactNode) => (
  <>
    {text}
    {icon}
  </>
);

export const getSShiftPrimaryText = (shiftData: SShift) => {
  const { shiftType, isTravel } = shiftData;
  const text = sShiftTypeNames[shiftType] ?? null;

  switch (shiftData.shiftType) {
    // case SShiftType.OnCall:
    //   return `${text}${unavailabilities?.length ? ' (N/A)' : ''}`;
    case SShiftType.OnCall:
      return isTravel ? getTextWithIcon(text, <MdOutlineLuggage size={16} />) : text;
    default:
      return text;
  }
};

export const getSShiftSecondaryText = (shiftData: SShift, isSuffix?: boolean) => {
  const organNames = shiftData.organs?.map((o) => (isSuffix ? organSuffixes[o] : o)).join(', ') ?? null;

  switch (shiftData.shiftType) {
    case SShiftType.OnCall:
      return organNames;
    default:
      return null;
  }
};

export const getSShiftDropdownOptionIconColor = (shiftType?: SShiftType) => {
  switch (shiftType) {
    case SShiftType.OnCall:
      return 'text-base-20';
    case SShiftType.Vacation:
    case SShiftType.Sick:
      return 'text-warning-40';
    case SShiftType.Unavailable:
      return 'text-danger-30';
    case SShiftType.NoCall:
      return 'text-base-30';
    default:
      return 'text-base-5';
  }
};

const S_WD_SHIFT_TYPES = new Set([SShiftType.Unavailable, SShiftType.Sick, SShiftType.Vacation]);

export const getSShowHrIcon = (shiftData: SShift): boolean =>
  !!shiftData.isHrShift && S_WD_SHIFT_TYPES.has(shiftData.shiftType);
